import { Badge } from "@components/ui/Badge"
import { formatDate, formatDateTime, formatRelative } from "@lib/intl"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import type { InviteStatus } from "@/types"

const BADGE: Record<InviteStatus, "success" | "attention" | "danger" | "neutral" | "outline"> = {
  accepted: "success",
  invited: "attention",
  // Needs a new link to do anything: the one state that asks for action.
  expired: "danger",
  revoked: "neutral",
  not_invited: "outline",
}

/**
 * Where an account stands, as a badge and a sentence (PRD-60 chunk B, PRD-56
 * §FR-13). Shared by Profesores and Usuarios, which invite the same way.
 *
 *   invited     → "Invitación pendiente" · "enviada hace 2 días"
 *   expired     → "Invitación vencida" · "venció hace 3 días"
 *   revoked     → "Invitación anulada"
 *   not_invited → "Sin invitación"
 *   accepted    → "Cuenta activa" · "desde el 12 sep 2026"
 *   trash       → "En papelera" · "hace 3 días"
 *
 * "Pendiente" used to cover a link that stopped working a week ago.
 */
export function AccountStatus({
  status,
  invitedAt,
  acceptedAt,
  expiresAt,
  deletedAt,
}: {
  status: InviteStatus
  invitedAt?: string | null
  acceptedAt?: string | null
  expiresAt?: string | null
  deletedAt?: string | null
}) {
  const { t } = useTranslation("common")

  if (deletedAt) {
    return (
      <Line
        badge={<Badge variant="neutral">{t("badge.deleted")}</Badge>}
        detail={t("account_status.deleted_when", { when: formatRelative(deletedAt) })}
        exact={deletedAt}
      />
    )
  }
  const detail =
    status === "accepted" && acceptedAt
      ? { text: t("account_status.active_since", { date: formatDate(acceptedAt) }), at: acceptedAt }
      : status === "invited" && invitedAt
        ? {
            text: t("account_status.invited_when", { when: formatRelative(invitedAt) }),
            at: invitedAt,
          }
        : status === "expired" && expiresAt
          ? {
              text: t("account_status.expired_when", { when: formatRelative(expiresAt) }),
              at: expiresAt,
            }
          : null
  return (
    <Line
      badge={<Badge variant={BADGE[status]}>{t(`account_status.${status}`)}</Badge>}
      detail={detail?.text ?? null}
      exact={detail?.at}
    />
  )
}

function Line({
  badge,
  detail,
  exact,
}: {
  badge: ReactNode
  detail: string | null
  exact?: string | null
}) {
  return (
    <span className="flex flex-col items-start gap-1">
      {badge}
      {detail && (
        <span className="text-meta" title={exact ? formatDateTime(exact) : undefined}>
          {detail}
        </span>
      )}
    </span>
  )
}
